import { useForm, Link } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft } from 'lucide-react'

interface Client {
  id: number
  name: string
  code: string
  notes: string | null
}

interface Props {
  client: Client
  errors: Record<string, string[]>
}

export default function ClientEdit({ client, errors }: Props) {
  const { data, setData, patch, processing } = useForm({
    client: {
      name: client.name,
      code: client.code,
      notes: client.notes || '',
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/clients/${client.id}`)
  }

  const formData = data.client
  const setFormData = (key: keyof typeof formData, value: string) => {
    setData('client', { ...formData, [key]: value })
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link href={`/clients/${client.id}`}>
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edit Client</h1>
            <p className="text-muted-foreground">
              Update the details for {client.name}.
            </p>
          </div>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Client Details</CardTitle>
            <CardDescription>
              Make changes to the client details below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData('name', e.target.value)}
                  autoFocus
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData('code', e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  A unique identifier for this client (lowercase, no spaces).
                </p>
                {errors.code && (
                  <p className="text-sm text-destructive">{errors.code[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData('notes', e.target.value)}
                  rows={4}
                />
                {errors.notes && (
                  <p className="text-sm text-destructive">{errors.notes[0]}</p>
                )}
              </div>

              <div className="flex gap-4">
                <Button type="submit" disabled={processing}>
                  {processing ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" asChild>
                  <Link href={`/clients/${client.id}`}>Cancel</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
